import { useMutation, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { errorFromResponse } from '@/lib/errors';
import { ApiClientError, idempotencyKeyFor } from '@/lib/http-core';
import { orderQueryKey } from './useOrderQuery';

/**
 * QA-432 — PG(토스) 결제 상태 동기화. 토스 상점관리자에서 직접 취소·환불한 주문이 BO 에 `결제완료` 로 남는 문제를
 * 운영자가 바로잡는다: BE 가 토스에 결제를 다시 조회해 취소·환불이면 주문을 취소완료로 바꾸고 마일리지·재고를 되돌린다.
 * 응답 계약은 BE 전달 문서(BO-TO-BE-2026-10-20-order-pg-cancel-sync.md) 제안안.
 */
export interface PaymentSyncResult {
  /** 동기화로 주문·결제가 바뀌었는가. false = 이미 PG 와 같음. */
  changed: boolean;
  /** 토스 기준 결제 상태(예 DONE / CANCELED / PARTIAL_CANCELED). */
  pgStatus: string | null;
  /** 동기화 후 주문 상태(예 CANCELED). */
  orderStatus: string | null;
  /**
   * order-service #61 — true 면 자동 취소하지 않았다(부분 환불·출고 후·진행 중 클레임). 결제 상태만 반영하고
   * reviewReason(한국어 문구)을 운영자에게 안내한다.
   */
  reviewRequired: boolean;
  reviewReason: string | null;
}

/** BE 반영 전(엔드포인트 없음) — 코드 없는 404/405. */
export class PaymentSyncNotReadyError extends Error {}

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v : null);

export const useOrderPaymentSyncMutation = (orderId: string) => {
  const qc = useQueryClient();
  return useMutation<PaymentSyncResult, Error, void>({
    mutationFn: async () => {
      const path = `/api/admin/v1/orders/${encodeURIComponent(orderId)}/payment-sync`;
      const res = await adminFetch(path, {
        method: 'POST',
        headers: { 'Idempotency-Key': idempotencyKeyFor('POST', path, { at: Date.now() }) },
      });
      if (res.status === 405) throw new PaymentSyncNotReadyError('not ready');
      if (!res.ok) {
        const err = await errorFromResponse(res);
        // 라우트가 없으면 본문 없는 404(HTTP_404) 또는 게이트웨이 공통 404 — 주문이 없다는 BE 코드(ORDER_NOT_FOUND 등)와 구분한다.
        const code = err instanceof ApiClientError ? String(err.code) : '';
        if (res.status === 404 && (code === '' || code.startsWith('HTTP_') || code === 'COMMON_NOT_FOUND')) {
          throw new PaymentSyncNotReadyError('not ready');
        }
        throw err;
      }
      const env = (await res.json().catch(() => ({}))) as { data?: Record<string, unknown> | null };
      const d = env.data ?? {};
      return {
        changed: d.changed === true,
        pgStatus: str(d.pgStatus),
        orderStatus: str(d.orderStatus),
        reviewRequired: d.reviewRequired === true,
        reviewReason: str(d.reviewReason),
      };
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: orderQueryKey(orderId) });
      await qc.invalidateQueries({ queryKey: ['admin', 'orders'] });
    },
  });
};
